/**
 * The case file's folded record blocks — receipts, provenance, the authority
 * footnote, source rows, and the runs roll-up — extracted verbatim from
 * CaseFile (YAG-440) so the review case body renders the same record the
 * generic case file does. No behavior change.
 */
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import type { Run, WorkItem, WorkItemRunListEntry } from "@yagni/shared";
import { canRunAgain, jobLineLabel, runUrl } from "@yagni/shared";
import { formatMillicents, RUN_TONE, runStatusLabel } from "./useWorkItemRuns.js";
import { itemCostLine } from "./workCopy.js";
import { useShowsRunCost } from "../hooks/useShowsRunCost.js";
import { safeExternalUrl } from "../lib/safeUrl.js";
import { runLabel } from "./runLabel.js";
import { RunAgainButton } from "./pageActs.js";
import type { WorkerLookup } from "./useWorkerLookup.js";

/* ── linked external issue (one-way Linear/Jira mirror) ── */

const ISSUE_HOST_RE = /(linear\.app|atlassian\.net)/i;
const ISSUE_KEY_RE = /\b([A-Z][A-Z0-9]+-\d+)\b/;

/**
 * Derive the linked-issue chip from the free-form `source_ref` the creator
 * recorded — the same provenance SourceBlock renders. We surface a header chip
 * only when a Linear/Jira URL is present; the label is the issue key parsed
 * from the URL when it's there, else a neutral "Issue". Returns null otherwise
 * so non-mirrored items show no chip.
 */
export function findLinkedIssue(item: WorkItem): { url: string; label: string } | null {
  const ref = item.sourceRef;
  if (!ref || typeof ref !== "object") return null;
  for (const v of Object.values(ref as Record<string, unknown>)) {
    if (typeof v === "string" && ISSUE_HOST_RE.test(v)) {
      const key = ISSUE_KEY_RE.exec(v.toUpperCase())?.[1];
      return { url: v, label: key ?? "Issue" };
    }
  }
  return null;
}

/** The tracker issue a ticket is linked to, as the sheet's header links it. */
export interface TrackerIssueLink {
  url: string;
  label: string;
  source: "linear" | "jira" | null;
}

const TRACKER_NAMES = { linear: "Linear", jira: "Jira" } as const;

function trackerSourceOfUrl(url: string): "linear" | "jira" | null {
  if (/linear\.app/i.test(url)) return "linear";
  if (/atlassian\.net/i.test(url)) return "jira";
  return null;
}

/**
 * The ticket's issue in Linear or Jira, in every stage: the push mirror's
 * page the list read joins (`trackerKey` + `trackerUrl`) when there is one,
 * else the issue URL its own provenance (`source_ref`) recorded. Only an
 * http(s) URL is linked; null when the ticket lives only in YAGNI.
 */
export function trackerIssueLink(item: WorkItem): TrackerIssueLink | null {
  const mirrored = item.trackerKey ? safeExternalUrl(item.trackerUrl) : null;
  const found =
    mirrored && item.trackerKey ? { url: mirrored, label: item.trackerKey } : findLinkedIssue(item);
  if (!found) return null;
  const url = safeExternalUrl(found.url);
  if (!url || !/^https?:\/\//i.test(url)) return null;
  return {
    url,
    label: found.label,
    source: trackerSourceOfUrl(url) ?? item.trackerSource ?? null,
  };
}

/** The link's hover text: "Open ENG-3024 in Linear". */
export function trackerIssueTitle(link: TrackerIssueLink): string {
  return `Open ${link.label} in ${link.source ? TRACKER_NAMES[link.source] : "the tracker"}`;
}

/* ── source provenance: why this is on your desk ── */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Ref keys that are machine plumbing on every source, rendered specially or not at all. */
const SOURCE_REF_HIDDEN_KEYS = new Set([
  "source",
  "lane",
  "pageId",
  "rhythmId",
  // Retired 2026-09-16; rows minted before then still carry it.
  "deliverable",
  "acceptedScope",
  "sourceTicketRefs",
  "scoping",
  "externalId",
  "teamId",
]);

/**
 * Keys the mint-time `resolveTicketLink` spreads into a proposal-lane ref;
 * they back the ticket-ref chips there, so they never also render as rows.
 * On other sources (a Gmail thread's `url`) they are honest rows and render.
 */
const PROPOSAL_LINK_KEYS = new Set(["identifier", "url"]);

function sourceTicketRefsOf(ref: Record<string, unknown>): string[] {
  const raw = ref["sourceTicketRefs"];
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === "string" && v.trim().length > 0);
}

/**
 * The item's provenance, in human sentences instead of the old raw key/value
 * `source_ref` dump (which printed UUIDs and JSON arrays at the customer).
 *
 * - Proposal-lane items: who proposed it + a link to the full proposal page.
 * - `sourceTicketRefs` (the backlog tickets a proposal argues from, external
 *   keys like `LIN-123`): rendered as chips. A single matched ref carries a
 *   resolved `url`/`identifier` in the same ref (mint-time `resolveTicketLink`)
 *   and links out; unmatched keys stay honest plain text.
 * - Machine keys (lane, pageId, rhythmId, UUIDs, empty values) never render.
 *
 * The heading names the actual SOURCE SYSTEM: when the ref carries its own
 * `source` (e.g. linear), that wins; `item.source` is the origin CHANNEL the
 * item arrived through (manual/agent-logged) and renders as its own labeled
 * row instead — never two contradictory values under the one "SOURCE ·" label
 * ("SOURCE · manual" over `source: linear`, prod 2026-07-12).
 */
export function SourceBlock({ item, teamName }: { item: WorkItem; teamName?: string | null }) {
  const ref = item.sourceRef;
  if (!ref || typeof ref !== "object" || Object.keys(ref).length === 0) return null;
  const record = ref as Record<string, unknown>;
  const refSourceRaw = record["source"];
  const refSource =
    typeof refSourceRaw === "string" && refSourceRaw.trim() ? refSourceRaw : null;
  const heading = refSource ?? item.source;
  const isProposalLane = record["lane"] === "proposal_lane";
  const ticketRefs = sourceTicketRefsOf(record);
  const matchedUrl = typeof record["url"] === "string" ? record["url"] : null;
  const matchedKey =
    typeof record["identifier"] === "string" ? record["identifier"].toUpperCase() : null;

  const rows = Object.entries(record).filter(([k, v]) => {
    if (SOURCE_REF_HIDDEN_KEYS.has(k)) return false;
    if (isProposalLane && PROPOSAL_LINK_KEYS.has(k)) return false;
    if (v === null || v === undefined) return false;
    if (typeof v === "object") return false;
    if (typeof v === "string" && (v.trim() === "" || UUID_RE.test(v.trim()))) return false;
    return true;
  });

  return (
    <div className="wk-src">
      <div className="is-art-lab">
        Source{heading ? <span className="wk-src-sys"> · {heading}</span> : null}
      </div>
      {isProposalLane && (
        <p className="wk-src-line">
          Proposed by {teamName?.trim() || "the Team"} from its rhythm
        </p>
      )}
      {ticketRefs.length > 0 && (
        <div className="wk-src-refs" data-testid="source-ticket-refs">
          <span className="wk-src-refs-label">Argues from</span>
          {ticketRefs.map((key) => {
            const linked =
              matchedUrl != null &&
              (ticketRefs.length === 1 || key.toUpperCase() === matchedKey);
            return linked ? (
              <a
                key={key}
                className="wk-src-chip"
                href={matchedUrl}
                target="_blank"
                rel="noreferrer"
              >
                {key} <ExternalLink width={10} height={10} aria-hidden />
              </a>
            ) : (
              <span key={key} className="wk-src-chip">
                {key}
              </span>
            );
          })}
        </div>
      )}
      {(rows.length > 0 || (refSource && refSource !== item.source)) && (
        <dl className="wk-src-rows">
          {refSource && refSource !== item.source && (
            <div className="wk-src-row">
              <dt>origin</dt>
              <dd>{item.source === "manual" ? "logged manually" : `via ${item.source}`}</dd>
            </div>
          )}
          {rows.map(([k, v]) => (
            <div className="wk-src-row" key={k}>
              <dt>{k}</dt>
              <dd>
                {typeof v === "string" && /^https?:\/\//.test(v) ? (
                  <a href={v} target="_blank" rel="noreferrer">
                    {v} <ExternalLink width={11} height={11} aria-hidden />
                  </a>
                ) : (
                  String(v)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/* ── the receipts ledger: derived from the runs + PR the item already carries ──
   The dedicated per-act Receipt ledger (verify results + auto-shipped undo
   windows) has no read endpoint here yet (YAG-253); until it lands, the honest
   ledger is the completed runs and the merged/opened PR — every real. */

export interface ReceiptRow {
  fact: string;
  proof: string;
  undoNote?: string;
}

export function deriveReceipts(item: WorkItem, runs: Run[]): ReceiptRow[] {
  const rows: ReceiptRow[] = [];
  if (item.prUrl) {
    const merged = item.runStatus === "merged" || item.builderStatus === "merged";
    const num = typeof item.prNumber === "number" ? `#${item.prNumber}` : "";
    rows.push({
      fact: `Pull request ${num} ${merged ? "merged" : "opened"}`.replace(/\s+/g, " ").trim(),
      proof: item.commitSha
        ? `GitHub · commit ${item.commitSha.slice(0, 7)}`
        : item.branchName
          ? `GitHub · ${item.branchName}`
          : "GitHub",
    });
  }
  for (const run of runs) {
    if (run.status !== "complete" && run.status !== "partial") continue;
    rows.push({
      fact: run.outcomeSummary || `${runLabel(run, runs)} · ${runStatusLabel(run)}`,
      proof: `${run.kind} · ${run.runtimeProvider ?? "run"} · ${runStatusLabel(run)}`,
    });
    if (rows.length >= 6) break;
  }
  return rows;
}

export function Receipts({ receipts }: { receipts: ReceiptRow[] }) {
  return (
    <div className="cf-receipts" data-testid="case-file-receipts">
      {receipts.map((r, i) => (
        <div className="cf-receipt" key={i}>
          <span className="cf-receipt-mark" aria-hidden>
            <span />
          </span>
          <div className="cf-receipt-main">
            <div className="cf-receipt-fact">{r.fact}</div>
            <div className="cf-receipt-proof">{r.proof}</div>
            {r.undoNote && <div className="cf-receipt-undo">{r.undoNote}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── the runs roll-up rows ── */

// The old Provenance / "The facts" grid (initiator · run · cost · PR · filed)
// is gone: initiator lives in the byline and narrative, the PR and filed chips
// live in the header, and run + price live on the rows below (and on the Run
// detail page they link to). It was the third rendering of the same run.

function RunRow({
  run,
  runs = [],
  workItemId,
  onRunAgain,
  workerOf,
  purposeLead = false,
  statusLabel = runStatusLabel,
}: {
  run: WorkItemRunListEntry;
  /** Every run on the item, so a repeat reads "again" rather than a number. */
  runs?: readonly WorkItemRunListEntry[];
  workItemId: string;
  /** Start the line behind this run again. Absent hides the verb entirely. */
  onRunAgain?: (runId: string) => Promise<void>;
  /** Resolve the line's Agent to its display name. Absent reads the slug. */
  workerOf?: WorkerLookup;
  /** Lead the row with the kind of pass ("Build · again"), not its summary. */
  purposeLead?: boolean;
  /** The run's status word. The case file names a stopped build "stopped". */
  statusLabel?: (run: WorkItemRunListEntry) => string;
}) {
  const tone = RUN_TONE[run.status];
  // A workspace on the pull-request plan is not billed these dollars, so it
  // does not read them (`useShowsRunCost`). God Mode always does.
  const showsCost = useShowsRunCost();
  const price = showsCost ? formatMillicents(run.sellMillicents) : null;
  // Which standing job spent this (ADR-0044, plan Task 6). A run that predates
  // the cutover, or one no line started, carries no job and the row reads as
  // it always did.
  const jobLine = run.jobId
    ? jobLineLabel({
        agentName:
          (run.jobPersonaSlug ? workerOf?.(run.jobPersonaSlug)?.displayName : null) ??
          run.jobPersonaSlug ??
          null,
        kind: run.jobKind ?? null,
        instance: run.jobInstance ?? null,
      })
    : null;
  return (
    <div className={"wk-run is-" + tone}>
      <span className="wk-run-dot" aria-hidden />
      <div className="wk-run-main">
        <div className="wk-run-title">
          {purposeLead && run.purpose ? runLabel(run, runs) : run.outcomeSummary || runLabel(run, runs)}
        </div>
        <div className="wk-run-meta">
          <span className="wk-run-prov">{run.kind}</span>
          <span className="wk-run-sep" aria-hidden />
          <span className="wk-run-state">{statusLabel(run)}</span>
          {jobLine && (
            <>
              <span className="wk-run-sep" aria-hidden />
              <span className="wk-run-job" data-testid={`wk-run-job-${run.id}`}>
                {jobLine}
              </span>
            </>
          )}
          {price && (
            <>
              <span className="wk-run-sep" aria-hidden />
              <span>{price}</span>
            </>
          )}
        </div>
      </div>
      <div className="wk-run-acts">
        {/* Run again, on the runs that stopped short (edge case 31). The route
            is still the authority and refuses a finished run, a stale event, a
            line that is off and a run no line started; this only stops the
            page offering a verb it already knows will be refused. */}
        {onRunAgain && canRunAgain(run) && (
          <RunAgainButton runId={run.id} onRunAgain={onRunAgain} />
        )}
        <Link className="wk-run-btn" to={runUrl(workItemId, run.attemptNo)}>
          Open
        </Link>
      </div>
    </div>
  );
}

/**
 * What each worker on this item was charged for, in the order they spent it.
 *
 * The runs already carry `personaSlug` and their own sell price, so grouping
 * them is arithmetic on numbers somebody recorded, not an apportionment. A
 * run with no worker (a Team-owned pass, a scoping read nobody is named on)
 * groups under the Team's name rather than being dropped or given to whoever
 * happens to be first: the money was spent and the sentence has to add up.
 *
 * Order is by spend, largest first, so the sentence reads as an answer to
 * "where did it go" instead of an accident of run order.
 *
 * A share that rounds to nothing at the printed precision is left out rather
 * than printed as "$0.00": that reads as "this worker cost nothing", which is
 * a claim about pricing rather than the fact that their share is smaller than
 * a cent. The total above it is unchanged either way.
 */
export function workerCostShares(
  runs: WorkItemRunListEntry[],
  workerOf: WorkerLookup | undefined,
  teamName: string,
): { name: string; amount: string | null }[] {
  const totals = new Map<string, { name: string; millicents: number }>();
  const add = (slug: string | null, millicents: number) => {
    const key = slug ?? "";
    const name = (slug ? workerOf?.(slug)?.displayName : null) ?? (slug ? slug : teamName);
    const current = totals.get(key) ?? { name, millicents: 0 };
    current.millicents += millicents;
    totals.set(key, current);
  };
  for (const run of runs) {
    add(
      run.personaSlug ?? null,
      typeof run.sellMillicents === "number" ? run.sellMillicents : 0,
    );
  }
  return [...totals.values()]
    .sort((a, b) => b.millicents - a.millicents)
    .map((entry) => {
      const amount = formatMillicents(entry.millicents);
      return { name: entry.name, amount: amount === "$0.00" ? null : amount };
    });
}

export function RunsBlock({
  runs,
  loading,
  workItemId,
  totalSellMillicents,
  workerOf,
  teamName,
  onRunAgain,
  purposeLead,
  statusLabel,
}: {
  runs: WorkItemRunListEntry[];
  loading: boolean;
  workItemId: string;
  /** Start the line behind a stopped run again. Absent hides the verb. */
  onRunAgain?: (runId: string) => Promise<void>;
  /** What the workspace was charged across every run (sell side, never COGS). */
  totalSellMillicents?: number;
  /** Resolve a worker slug for the cost sentence. Absent falls back to slugs. */
  workerOf?: WorkerLookup;
  /** Where a run with no worker is filed. The cost still has to add up. */
  teamName?: string;
  /** Rows lead with the kind of pass. Off for the review case file. */
  purposeLead?: boolean;
  /** The status word per row. Absent reads the shared lexicon. */
  statusLabel?: (run: WorkItemRunListEntry) => string;
}) {
  const showsCost = useShowsRunCost();
  if (loading || runs.length === 0) return null;
  const active = runs.filter((r) => RUN_TONE[r.status] === "running").length;
  // Null on the pull-request plan: the total and the sentence below print
  // nothing, exactly as they do when nothing was charged.
  const total = showsCost ? formatMillicents(totalSellMillicents) : null;
  // One sentence, from the total the roll-up already carries and the per-run
  // prices behind it. Nothing prints when the total is zero: "$0.00" is a
  // claim about pricing, not a fact about this work.
  const cost = itemCostLine(total, workerCostShares(runs, workerOf, teamName ?? "the Team"));
  return (
    <div className="wk-runs">
      <div className="is-art-lab">
        Runs{" "}
        <span className="wk-runs-c">
          {runs.length} · {active} active
          {/* The charged total also opens the cost sentence below. It stays
              here because the roll-up's own head has always carried it and
              other surfaces read it there; the sentence is what a buyer
              reads, this is what an operator scans. */}
          {total ? ` · ${total} total` : ""}
        </span>
      </div>
      {cost && (
        <p className="wk-runs-cost" data-testid="case-file-cost">
          {cost}
        </p>
      )}
      <div className="wk-runs-list">
        {runs.map((r) => (
          <RunRow
            key={r.id}
            run={r}
            runs={runs}
            workItemId={workItemId}
            workerOf={workerOf}
            {...(onRunAgain ? { onRunAgain } : {})}
            {...(purposeLead ? { purposeLead } : {})}
            {...(statusLabel ? { statusLabel } : {})}
          />
        ))}
      </div>
    </div>
  );
}
